import { createSignal, Show, useContext } from 'solid-js';
import { createStore } from 'solid-js/store';
import { useNavigate } from '@solidjs/router';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { getCropQuery, getCropsQuery, getCropTypeCategoriesQuery } from '../../graphql/queries/crops';
import { getProductsQuery } from '../../graphql/queries/products';
import { getTreatmentQuery } from '../../graphql/queries/treatments';
import { getStrategiesSearchQuery } from '../../graphql/queries/strategies';
import type { Strategy } from '../strategies/strategies.d';
import type { Crop } from '../crops/crops.d';
import type { Treatment } from '../treatments/treatments.d';
import type { CropTypeCategory } from '../start/start-types';
import {
    filterPurposesWithTreatments,
    filterRelatedModels,
    getAllProductsForStrategies,
    getFilteredStrategies,
    getRelatedModelsCount,
} from './utils';
import { DropdownList } from './components/dropdown-list/dropdown-list';
import { Results } from './components/results/results';
import { SearchBar } from './components/search-bar/search-bar';
import { StyledLoadingMessage, StyledSearchContainer, StyledSearchContainerInner } from './search.style';
import Button from '../button/button';
import { Row } from '../layout/row/row';
import { GrowthStages } from '../start/growth-stages/growth-stages';
import { getPurposesQuery } from '../../graphql/queries/purpose';

const limit = 500;

export type SearchModel = 'crop' | 'treatment' | 'product' | 'purpose';

export type SearchApiResponse = {
    name: string;
    slug: string;
};

export type PurposeType = SearchApiResponse & {
    treatments: SearchApiResponse[];
};

export type FilterTypeStore = {
    searchTerm: string;
    secondarySearchTerm: string;
    crop: (Crop & { position: number }) | null;
    treatment: (Treatment & { position: number }) | null;
    purpose: (PurposeType & { position: number }) | null;
    crops: SearchApiResponse[];
    products: SearchApiResponse[];
    activeIngredientProducts: SearchApiResponse[];
    treatments: SearchApiResponse[];
    purposes: PurposeType[];
    strategies: {
        results: Strategy[];
        crops: SearchApiResponse[];
        products: SearchApiResponse[];
        treatments: SearchApiResponse[];
        purposes: PurposeType[];
        // The total number of unique crops, products and treatments returned
        // when we select a crop or treatment from the intial search
        totals: {
            crops: number;
            products: number;
            treatments: number;
        };
        // The resultant filtered strategies and products when both a crop AND a treatment are selected
        filtered: {
            strategies: Strategy[];
            products: SearchApiResponse[];
            pagination: {
                visible: boolean;
                limit: number;
                page: number;
            };
        };
    };
};

export const Search = () => {
    const navigate = useNavigate();
    const { graphQlClient, createCachedResource, isSearchOpen, setIsSearchOpen } = useContext(AppContext);
    const [cropTypeCategoriesData] = createCachedResource(getCropTypeCategoriesQuery);
    const [purposesData] = createCachedResource(getPurposesQuery, { limit: 100 });
    const [isLoading, setIsLoading] = createSignal(false);

    const getInitialState = () => ({
        searchTerm: '',
        secondarySearchTerm: '',
        crop: null,
        treatment: null,
        purpose: null,
        crops: [],
        products: [],
        activeIngredientProducts: [],
        treatments: [],
        purposes: [],
        strategies: {
            results: [],
            crops: [],
            products: [],
            treatments: [],
            purposes: [],
            totals: {
                crops: 0,
                products: 0,
                treatments: 0,
            },
            filtered: {
                strategies: [],
                products: [],
                pagination: {
                    visible: false,
                    limit: 10,
                    page: 1,
                },
            },
        },
    });
    const [filters, setFilters] = createStore<FilterTypeStore>(getInitialState());
    const categories = (): CropTypeCategory[] => cropTypeCategoriesData()?.cropTypeCategories?.rows || [];

    const removeSelection = (position = 1) => {
        if (filters.crop?.position === position) {
            removeCrop();
            return;
        }

        if (filters.purpose?.position === position) {
            removePurpose();
            return;
        }

        removeTreatment();
    };

    const removeCrop = () => {
        if (filters.crop?.position === 2) {
            setFilters({ crop: null });
            resetProducts();
            return;
        }

        setFilters(getInitialState());

        if (filters.treatment) {
            selectedModalCallback(filters.treatment.slug, 'treatment');
        }
        if (filters.purpose) {
            selectedModalCallback(filters.purpose.slug, 'purpose');
        }
    };

    const removeTreatment = () => {
        if (filters.treatment?.position === 2) {
            setFilters({ treatment: null });
            resetProducts();
            return;
        }

        setFilters(getInitialState());
        if (filters.crop) {
            selectedModalCallback(filters.crop.slug, 'crop');
        }
    };

    const removePurpose = () => {
        if (filters.purpose?.position === 2) {
            setFilters({ purpose: null });
            resetProducts();
            return;
        }

        setFilters(getInitialState());
        if (filters.crop) {
            selectedModalCallback(filters.crop.slug, 'crop');
        }
    };

    const resetProducts = () => {
        const filteredProducts = getAllProductsForStrategies(filters.strategies.results);
        setFilters('strategies', 'filtered', {
            strategies: [],
            products: filteredProducts,
            pagination: {
                visible: false,
                limit: 10,
                page: 1,
            },
        });

        secondarySearch();
    };

    const search = (term = '') => {
        setFilters({ searchTerm: term });

        if (term.length < 2) {
            setFilters('products', []);
            setFilters('treatments', []);
            setFilters('crops', []);
            return;
        }

        getProducts();
        getCrops();
        getPurposesAndTreatments();
    };

    const secondarySearch = async (term = '') => {
        if (term !== 'ALL') {
            setFilters({ secondarySearchTerm: term });

            // Reset the filters if the term is empty
            if (!term.length) {
                setFilters('strategies', 'products', []);
                setFilters('strategies', 'treatments', []);
                setFilters('strategies', 'purposes', []);
                setFilters('strategies', 'crops', []);
                return;
            }
        }

        const purposeData = (): PurposeType[] => purposesData()?.purposes?.rows || [];

        const { filteredCrops, filteredProducts, filteredPurposes } = filterRelatedModels(
            filters.strategies.results,
            purposeData(),
            filters.secondarySearchTerm,
            filters.crop ? 'crop' : filters.treatment ? 'treatment' : 'purpose'
        );

        setFilters('strategies', 'products', filteredProducts);
        setFilters('strategies', 'purposes', filteredPurposes);
        setFilters('strategies', 'crops', filteredCrops);
    };

    const getProducts = async () => {
        const { products } = await graphQlClient(getProductsQuery, { searchTerm: filters.searchTerm, limit });
        const { products: activeIngredientProducts } = await graphQlClient(getProductsQuery, {
            ingredientSearchTerm: filters.searchTerm,
            limit,
        });

        setFilters({
            products: products.rows,
            activeIngredientProducts: activeIngredientProducts.rows.sort((a: SearchApiResponse, b: SearchApiResponse) =>
                a.name.localeCompare(b.name)
            ),
        });
    };

    const getCrops = async () => {
        const { crops } = await graphQlClient(getCropsQuery, { searchTerm: filters.searchTerm, limit });
        setFilters({ crops: crops.rows });
    };

    const getPurposesAndTreatments = async () => {
        const purposeData = (): PurposeType[] => purposesData()?.purposes?.rows || [];
        const purposes = filterPurposesWithTreatments(purposeData(), filters.searchTerm);
        setFilters({ purposes });
    };

    const setSelectedModel = async (slug: string, type: Omit<SearchModel, 'product'> = 'crop', position = 1) => {
        if (type === 'crop') {
            setIsLoading(true);
            const { crop } = await graphQlClient(getCropQuery, { slug });
            setFilters({ crop: { ...crop, position } });
            setIsLoading(false);
            return;
        }

        if (type === 'treatment') {
            setIsLoading(true);
            const { treatment } = await graphQlClient(getTreatmentQuery, { slug });
            setFilters({ treatment: { ...treatment, position } });
            setIsLoading(false);
            return;
        }

        // Reset the purposes filters
        if (position === 2) {
            setFilters({ purposes: purposesData()?.purposes?.rows || [] });
        }

        const purpose = filters.purposes.find((p) => p.slug === slug);
        if (purpose) {
            setFilters({ purpose: { ...purpose, position } });
        }
    };

    const getStrategies = async () => {
        setIsLoading(true);

        const params = {
            cropSlug: filters.crop?.slug || '',
            treatmentSlug: filters.treatment?.slug || '',
        };

        // If they've selected a purpose, include its treatments in the query
        if (filters.purpose) {
            params.treatmentSlug = filters.purpose.treatments.map((treatment) => treatment.slug).join(',');
        }

        // Get the strategies via either crop or treatment slug
        const { strategies }: { strategies: Strategy[] } = await graphQlClient(getStrategiesSearchQuery, params);
        setFilters('strategies', 'results', strategies);

        // Count respective related models
        const { crops, treatments, products } = getRelatedModelsCount(strategies);
        setFilters('strategies', 'totals', { crops, products, treatments });

        // Get all related products and reset product pagination
        const filteredProducts = getAllProductsForStrategies(strategies);
        setFilters('strategies', 'filtered', {
            strategies: [],
            products: filteredProducts,
            pagination: {
                visible: false,
                limit: 10,
                page: 1,
            },
        });

        setIsLoading(false);
    };

    const getResultsFromStrategies = async () => {
        let filteredStrategies: Strategy[] = [];

        if (filters.crop && (filters.treatment || filters.purpose)) {
            filteredStrategies = getFilteredStrategies(filters.strategies.results, {
                crop: filters.crop,
                treatment: filters.treatment,
                purpose: filters.purpose,
            });
        }
        const filteredProducts = getAllProductsForStrategies(filteredStrategies);

        setFilters('strategies', 'filtered', {
            strategies: filteredStrategies,
            products: filteredProducts,
            pagination: {
                visible: true,
                limit: 10,
                page: 1,
            },
        });
    };

    const selectedModalCallback = async (slug: string, model: SearchModel) => {
        if (model === 'product') {
            exitSearch(`/kategorier/produkter/${slug}`);
            return;
        }

        if (isLoading()) {
            return;
        }

        const position = !filters.purpose && !filters.treatment && !filters.crop ? 1 : 2;
        await setSelectedModel(slug, model, position);

        if (position === 1) {
            getStrategies();
            return;
        }

        getResultsFromStrategies();
    };

    const exitSearch = (to?: string) => {
        setIsSearchOpen(false);
        setFilters(getInitialState());

        if (to) {
            navigate(to);
        }
    };

    return (
        <Show when={isSearchOpen()}>
            <StyledSearchContainer>
                <StyledSearchContainerInner>
                    <Row justifyContent="end">
                        <Button disabled={isLoading()} label="Stäng" onClick={() => exitSearch()} />
                    </Row>
                    <SearchBar
                        search={search}
                        filters={filters}
                        setFilters={setFilters}
                        isLoading={isLoading()}
                        removeSelection={removeSelection}
                        secondarySearch={secondarySearch}
                    />
                    <DropdownList isLoading={isLoading()} filters={filters} onClick={selectedModalCallback} />
                    <Show when={!isLoading()} fallback={<StyledLoadingMessage>Laddar resultat...</StyledLoadingMessage>}>
                        <Results filters={filters} setFilters={setFilters} categories={categories()} exitSearch={exitSearch} />
                    </Show>
                </StyledSearchContainerInner>
                <GrowthStages />
            </StyledSearchContainer>
        </Show>
    );
};
